import { useEffect, type FC } from 'react';
import { useParams } from 'react-router-dom';
import 'Lessons/angularModule';
import { useCurrentUser, useNavigationHelper, useOfflineModeManager } from 'FrontRoyalAngular';
import { useTargetBrandConfig } from 'FrontRoyalAngular/useTargetBrandConfig';
import { useErrorLogService } from 'ErrorLogging';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { type StreamForStreamDashboard } from 'Lessons';
import { type CurrentUser } from 'Users';
import { useStreamForDashboard } from '../hooks/useStreamForDashboard';
import { useStreamWithLiveProgress } from '../hooks/useStreamWithLiveProgress';
import { useCompletionSettled } from '../hooks/useCompletionSettled';
import { StreamDashboardLoadError } from './StreamDashboardLoadError';
import { useStreamCompletedHeader } from '../hooks/useStreamCompletedHeader';
import { useContinueFromCompleted } from '../hooks/useContinueFromCompleted';
import { useEnterKeyPress } from '../hooks/useEnterKeyPress';
import { useCertificateImageSrc } from '../hooks/useCertificateImageSrc';
import { useShareCompletedStream } from '../hooks/useShareCompletedStream';
import { useIguanaUserFlags } from '../hooks/useIguanaUserFlags';
import { getCertificateImageSrc, isStreamComplete } from '../selectors';
import { StreamCompletedPage } from './StreamCompletedPage';

/*
    The completed route. It redirects an unfinished course, after useCompletionSettled, and reports a
    missing certificate. Visitors take a separate branch: useContentProgress suspends without a user.
*/

const StreamCompletedWithStream: FC<{ stream: StreamForStreamDashboard }> = ({ stream }) => {
    const offlineModeManager = useOfflineModeManager();
    const targetBrandConfig = useTargetBrandConfig();
    const ErrorLogService = useErrorLogService();
    const { canShare, blueOcean } = useIguanaUserFlags();
    const { onContinue, isContinuing } = useContinueFromCompleted(stream);
    const share = useShareCompletedStream(stream, !!blueOcean);
    const certificateImageSrc = useCertificateImageSrc(stream);

    // Skip an unstarted course, or this reports before the redirect guard settles.
    useEffect(() => {
        if (!stream.lessonStreamsProgress) return;
        if (getCertificateImageSrc(stream) || offlineModeManager.inOfflineMode) return;
        ErrorLogService.notify(new Error('Stream completed page rendered without a certificate.'));
    }, [stream, offlineModeManager, ErrorLogService]);

    // click-key="ENT" on the CONTINUE button, which advanced the screen without it being focused.
    useEnterKeyPress(onContinue, !isContinuing);

    return (
        <StreamCompletedPage
            stream={stream}
            isOffline={offlineModeManager.inOfflineMode}
            canShare={!!canShare}
            brandName={targetBrandConfig?.brandNameShort ?? ''}
            onShare={share}
            onContinue={onContinue}
            isContinuing={isContinuing}
            certificateImageSrc={certificateImageSrc}
        />
    );
};

const StudentStreamCompleted: FC<{ streamId: string; currentUser: CurrentUser }> = ({ streamId, currentUser }) => {
    const { stream, isLoading, hasError, retry } = useStreamForDashboard(streamId);
    const streamWithProgress = useStreamWithLiveProgress(stream);
    const { loadRoute } = useNavigationHelper();
    const isSettled = useCompletionSettled(currentUser, streamId);

    const isComplete = !!streamWithProgress && isStreamComplete(streamWithProgress);

    useEffect(() => {
        if (!streamWithProgress || !isSettled || isComplete) return;
        loadRoute(`/course/${streamWithProgress.id}`);
    }, [streamWithProgress, isSettled, isComplete, loadRoute]);

    // Same reason as StreamDashboardComponent: without this branch a failed fetch spins forever.
    if (hasError) return <StreamDashboardLoadError onRetry={retry} />;

    if (isLoading || !streamWithProgress) return <FrontRoyalSpinner className="fixed" />;

    return <StreamCompletedWithStream stream={streamWithProgress} />;
};

const VisitorStreamCompleted: FC<{ streamId: string }> = ({ streamId }) => {
    const { loadRoute } = useNavigationHelper();

    useEffect(() => {
        loadRoute(`/course/${streamId}`);
    }, [streamId, loadRoute]);

    return <FrontRoyalSpinner className="fixed" />;
};

export const StreamCompletedComponent: FC = () => {
    const { streamId } = useParams<{ streamId: string }>();
    const currentUser = useCurrentUser();

    useStreamCompletedHeader();

    if (!currentUser) return <VisitorStreamCompleted streamId={streamId ?? ''} />;
    return <StudentStreamCompleted streamId={streamId ?? ''} currentUser={currentUser} />;
};
